import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAbility } from '@casl/react';
import { Button, Col, Row } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';
import { ComponentDataTable, DefaultModal, SimpleCenteredModal, Icon } from '../../components';
import { AbilityContext } from '../../config/abilityContext';
import { downloadFile } from '../../services/utils';
import { sendAlert } from '../../actions/utils';
import ReportActions from '../Legalreports/ReportActions';
import { debounceGenerateMissingTimeReports } from '../../requests/attendaceRecord';
import MissingTimeReportFilterDataTable from './MissingTimeReportFilterDataTable';
import columns from './Columns';
import MissingTimeReportFilters from './MissingTimeReportFilters';

export const WorkdayExpander = props => {
  const { data, setDocumentModalBody, setDocumentModalShow, resource } = props;
  const { attendances, attendances_total: attendancesTotal } = data;

  return (
    <MissingTimeReportFilterDataTable
      attendances={attendances}
      attendancesTotal={attendancesTotal}
      setDocumentModalBody={setDocumentModalBody}
      setDocumentModalShow={setDocumentModalShow}
      resource={resource}
    />
  );
};

const MissingTimeReportDataTable = ({ rrhh, resource, filters: propFilters, bossFilters, isBoss }) => {
  const [amount, setAmount] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [isSubmitting, setSubmitting] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [localMoreData, setLocalMoreData] = useState(false);
  const [onRequest, setOnRequest] = useState(false);
  const [searchFirstStart, setSearchFirstStart] = useState(false);
  const [tile, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [parameters, setParameters] = useState([]);
  const [filters, setFilters] = useState();
  const [showFilter, setShowFilter] = useState(true);
  const [documentModalShow, setDocumentModalShow] = useState(false);
  const [documentModalBody, setDocumentModalBody] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const dispatch = useDispatch();
  const ability = useAbility(AbilityContext);
  const componentRef = useRef();
  const { currentEmployee } = useSelector(state => state.auth);
  const [module, setModule] = useState('Reporte de Tiempo Faltante');

  const printInfo = useReactToPrint({
    content: () => componentRef.current
  });

  const handleFailureRequest = error => {
    setAmount(0);
    setEmployees([]);
    const message = error?.response?.data?.message || 'TRABAJADOR NO ENCONTRADO';
    dispatch(sendAlert({ kind: 'error', message }));
  };

  useEffect(() => {
    const moduleNames = {
      DelayReport: 'Reporte de Atraso',
      EarlyDeparturesReport: 'Reporte de Salidas Anticipadas'
    };
    if (moduleNames[resource]) setModule(moduleNames[resource]);
  }, [resource]);

  const handleRequest = (params, format = null, type = 'json') => {
    const responseType = params.type === '.docx' ? 'blob' : type;
    const responseFormat = format === '.print' ? '.json' : format;
    const customParams = {
      ...params,
      employee_id: rrhh ? params.employee_id : currentEmployee
    };
    setOnRequest(true);
    if (dateRange.length > 0) {
      debounceGenerateMissingTimeReports({
        dispatch,
        params: {
          display_start: 0,
          display_length: 15,
          ...customParams,
          sort_direction: 'asc',
          sort_column: 'name',
          is_dt: false,
          paginate: format === null,
          resource
        },
        format: responseFormat,
        responseType,
        successCallback: response => {
          const { data } = response;
          if (format === '.xlsx' || format === '.docx' || format === '.pdf') {
            downloadFile(response);
          } else if (format === '.print') {
            setSubmitting(false);
            printInfo();
          } else {
            setAmount(data.metadata.amount);
            setEmployees(data.data);
          }
        },
        failureCallback: handleFailureRequest,
        callback: () => {
          setOnRequest(false);
          setSubmitting(false);
        }
      });
    } else {
      setOnRequest(false);
      setSubmitting(false);
    }
  };

  const handleMassAction = action => {
    const actions = {
      download_excel: { format: 'Excel', type: '.xlsx' },
      download_pdf: { format: 'PDF', type: '.pdf' }
    };

    const actionConfig = actions[action];
    if (actionConfig) {
      setTitle(`Exportar ${module}`);
      setBody(`¿Estás seguro que quieres exportar ${module} en formato ${actionConfig.format}?`);
      setParameters([{ ...filters, type: actionConfig.type }, actionConfig.type, 'text']);
      setModalShow(true);
    }
  };

  const handleFilterAdvance = async params => {
    setFilters(params);
    setDateRange(params.range_date_workday);
    setSubmitting(true);
    switch (params.type) {
      case null:
        setSearchFirstStart(true);
        setLocalMoreData(!localMoreData);
        setShowFilter(false);
        break;
      case '.xlsx':
        setTitle(`Exportar ${module}`);
        setBody(`¿Estás seguro que quieres exportar ${module} en formato Excel?`);
        setParameters([params, '.xlsx', 'text']);
        setModalShow(true);
        break;
      case '.pdf':
        setTitle(`Exportar ${module}`);
        setBody(`¿Estás seguro que quieres exportar ${module} en formato PDF?`);
        setParameters([params, '.pdf', 'text']);
        setModalShow(true);
        break;
      default:
        break;
    }
  };

  const handleRawFilters = () => {
    const rawFilters = propFilters || bossFilters;
    console.log('rawFilters', rawFilters);
    if (!rawFilters || Object.keys(rawFilters).length === 0) return;

    // map incoming `employee_ids` -> `filter_employee_ids` for compatibility
    const effectiveFilters = { ...rawFilters };
    if (Array.isArray(rawFilters.employee_ids) && rawFilters.employee_ids.length > 0) {
      effectiveFilters.filter_employee_id = null;
      delete effectiveFilters.filter_employee_id;
      delete effectiveFilters.report_type;
    }

    handleFilterAdvance(effectiveFilters);
  };

  const sortColumnCase = name => {
    switch (name) {
      case 'branch_office_address':
        return { sort_branch_offices: name };
      default:
        return { sort_column: name };
    }
  };

  useEffect(handleRawFilters, [propFilters, bossFilters]);

  return (
    <>
      <div className={showFilter ? '' : 'd-none'}>
        <MissingTimeReportFilters
          filterRRHH={rrhh}
          formRequest={handleFilterAdvance}
          isSubmit={isSubmitting}
          onlyPreview
        />
      </div>

      <div className={showFilter ? 'd-none' : ''}>
        {!isBoss && (
          <Row>
            <Col md={6} className="mt-3">
              <Button variant="primary" onClick={() => setShowFilter(true)}>
                <Icon icon="chevron-back" width={15} />
                <span className="arial-dt">Volver al filtro</span>
              </Button>
            </Col>
          </Row>
        )}

        <ComponentDataTable
          onRequest={onRequest}
          columns={columns()}
          moreData={localMoreData}
          data={employees}
          responsive
          totalRows={amount}
          handleSortCase={sortColumnCase}
          withMassActions={ability.can('export', resource)}
          massActions={
            <ReportActions
              attrr
              disabled={onRequest}
              handleClick={handleMassAction}
              dates={dateRange}
              showDatePeriod={false}
              downloadPdf
            />
          }
          resourceRequest={params => {
            handleRequest({ ...params, ...filters });
          }}
          expandableRows
          expandableRowExpanded={() => true}
          expandableRowsComponent={
            <WorkdayExpander
              setDocumentModalBody={setDocumentModalBody}
              resource={resource}
              setDocumentModalShow={setDocumentModalShow}
            />
          }
          searchFirstStart={searchFirstStart}
        />
      </div>

      <DefaultModal
        title={tile}
        body={body}
        show={modalShow}
        handleClose={() => {
          setModalShow(false);
          setSubmitting(false);
        }}
        handleConfirm={() => {
          handleRequest(...parameters);
          setModalShow(false);
        }}
        titleBtnClose="Cancelar"
        titleBtnSave="Confirmar"
      />
      <SimpleCenteredModal
        body={documentModalBody}
        onHide={() => setDocumentModalShow(false)}
        show={documentModalShow}
        size="xl"
      />
    </>
  );
};

export default MissingTimeReportDataTable;
